import moment from "moment";
import { FC, useEffect, useState } from "react";
import { Button, Col, Pagination, Row, Spinner, Table } from "react-bootstrap";
import { useAppDispatch } from "../../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";
import {
  getUserOrders,
  queryOrders,
  queryOrdersExport,
} from "../../../app/services/orderService";
import { Order } from "../../../models/responses/order";
import { RootState } from "../../../app/store";
import { useSelector } from "react-redux";
import { Check, Eye } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import Form from "react-bootstrap/Form";
import { IncomingOrdersStatistics } from "../../../models/responses/incomingOrdersStatistics";
import { getIncomingOrders } from "../../../app/services/statisticService";
import { ClientType } from "../../../models/enums/clientTypeEnum";

interface Props {}

const Dashboard: FC<Props> = () => {
  moment.locale("sv");
  moment.updateLocale(moment.locale(), { invalidDate: "-" });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const ordersByUserId: Order[] = useSelector(
    (state: RootState) => state.order.ordersByUserId
  );
  const loadingOrders: boolean = useSelector(
    (state: RootState) => state.order.isWaitingOrderGetResponse
  );
  const orders: Order[] = useSelector(
    (state: RootState) => state.order.queryOrderList || []
  );
  const setIsWaitingOrderExportResponse = useSelector(
    (state: RootState) => state.order.isWaitingOrderExportResponse
  );
  const incomingOrdersStatistics: IncomingOrdersStatistics = useSelector(
    (state: RootState) => state.statistics.incomingOrdersList
  );
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const total = useSelector((state: RootState) => state.order.totalQueryOrder);
  const totalPages = Math.ceil((total || 0) / pageSize);
  const [fromDateInput, setFromDateInput] = useState<Date>(new Date());
  const [toDateInput, setToDateInput] = useState<Date>(new Date());

  const clientType = useSelector(
    (state: RootState) => state.clientType.clientType
  );
  const clientTypeLabel =
    clientType === ClientType.Standard ? "Skadeverkstad" : clientType === ClientType.Catalog ? "AD Butik" : "Fleet";

  useEffect(() => {
    dispatch(getUserOrders(instance, account));
  }, [account, accounts, dispatch, instance]);

  useEffect(() => {
    dispatch(getIncomingOrders(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromDateInput(new Date(e.target.value));
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToDateInput(new Date(e.target.value));
  };

  const handleSearch = () => {
    dispatch(
      queryOrders(
        instance,
        account,
        undefined,
        page,
        pageSize,
        searchInput,
        "CreatedAt",
        true,
        false,
        clientType
      )
    );
  };

  const handleExport = () => {
    dispatch(queryOrdersExport(instance, account, fromDateInput, toDateInput, clientType));
  };

  const handleKeyDown = (e: { key: string; preventDefault: () => void }) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const openOrderHandler = (order: Order) => {
    navigate(`/orderStatus/${order.orderStatusId}/orders/view/${order.id}`, {
      relative: "path",
    });
  };

  const filterIncoming = (days: number | undefined) => {
    let from = new Date();
    if (days === undefined) from = new Date(0);
    else from.setDate(from.getDate() - days);

    from.setHours(0, 0, 0, 0);

    var match = incomingOrdersStatistics.orders.filter((o) => {
      return moment(o.key).isSameOrAfter(from);
    });

    if (!match || match.length === 0) return 0;

    return match.reduce((sum, o) => sum + o.value, 0);
  };

  return (
    <div>
      <div>
        <h2 className="mb-3">AD One</h2>
        <p>
          Här kan du hämta en CSV-fil med order, söka efter order och se dina
          aktiva order.
        </p>
        <hr />
      </div>
      <div>
        <h2 className="mb-3">Exportera order - {clientTypeLabel} </h2>
        <p>Hämta en CSV-fil filtrerad på leveransdatum</p>
        <Row>
          <Col xs={12} md={4}>
            <Form.Control
              type="date"
              placeholder="Från"
              value={
                fromDateInput ? fromDateInput.toISOString().split("T")[0] : ""
              }
              onChange={handleFromDateChange}
              onKeyDown={handleKeyDown}
            />
          </Col>
          <Col xs={12} md={4}>
            <Form.Control
              type="date"
              placeholder="Till"
              value={toDateInput ? toDateInput.toISOString().split("T")[0] : ""}
              onChange={handleToDateChange}
              onKeyDown={handleKeyDown}
            />
          </Col>
          <Col xs={12} md={4}>
            <Button
              variant="outline-primary"
              onClick={handleExport}
              disabled={setIsWaitingOrderExportResponse}
            >
              Exportera order - {clientTypeLabel}
            </Button>
          </Col>
        </Row>
        <hr />
      </div>
      <div>
        <h2 className="mb-3">Sök order - {clientTypeLabel} </h2>
        <Form className="mb-3">
          <Row>
            <Col xs={12} md={4}>
              <Form.Control
                type="text"
                placeholder="Regnummer/IO-nummer"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </Col>
            <Col xs={12} md={2}>
              <Button variant="outline-primary" onClick={handleSearch}>
                Sök
              </Button>
            </Col>
          </Row>
        </Form>
        <div>
          <Table responsive striped bordered hover>
            <thead>
              <tr role="button">
                <th>Skapad</th>
                <th>Orderstatus</th>
                <th>Kund</th>
                <th>Leverans</th>
                <th>Prod.dag</th>
                <th>Reg.nr</th>
                <th>AO-nummer</th>
                <th>IO-nummer</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr
                  key={order.id}
                  role="button"
                  onClick={() => openOrderHandler(order)}
                >
                  <td>
                    {moment
                      .utc(order.createdAt)
                      .tz("Europe/Stockholm")
                      .format("yyyy-MM-DD HH:mm")}
                  </td>
                  <td>{order.orderStatus?.name}</td>
                  <td>{order.customer?.name}</td>
                  <td>
                    {moment
                      .utc(order.deliveryDate)
                      .tz("Europe/Stockholm")
                      .format("yyyy-MM-DD")}
                  </td>
                  <td>
                    {moment
                      .utc(order.bookingDate)
                      .tz("Europe/Stockholm")
                      .format("yyyy-MM-DD")}
                  </td>
                  <td>{order.registrationNumber}</td>
                  <td>{order.aoNumber}</td>
                  <td>{order.purchaseOrderNumber}</td>
                  <td align="center">
                    <Eye />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {loadingOrders && (
            <>
              <Row className="mb-3">
                <Col className="text-center">
                  <Spinner variant="primary" />
                </Col>
              </Row>
            </>
          )}
          <Pagination className="justify-content-center">
            <Pagination.First
              onClick={() => setPage(1)}
              disabled={page === 1}
            />
            <Pagination.Prev
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            />
            {[...Array(totalPages)].map((_, index) => {
              const pageIndex = index + 1;
              if (pageIndex >= page - 3 && pageIndex <= page + 3) {
                return (
                  <Pagination.Item
                    key={pageIndex}
                    active={pageIndex === page}
                    onClick={() => setPage(pageIndex)}
                  >
                    {pageIndex}
                  </Pagination.Item>
                );
              }
              return null;
            })}
            <Pagination.Next
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
            />
            <Pagination.Last
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            />
          </Pagination>
        </div>
      </div>
      <hr></hr>
      <div>
        <h2 className="mb-3">Inkomna order - {clientTypeLabel}</h2>
        <Table responsive striped bordered hover>
          <thead>
            <tr>
              <th>Idag</th>
              <th>7 dagar</th>
              <th>30 dagar</th>
              <th>1 år</th>
              <th>Totalt</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{filterIncoming(0)}</td>
              <td>{filterIncoming(7)}</td>
              <td>{filterIncoming(30)}</td>
              <td>{filterIncoming(365)}</td>
              <td>{incomingOrdersStatistics.total}</td>
            </tr>
          </tbody>
        </Table>
      </div>
      <hr></hr>
      <div>
        <h2 className="mb-3">Aktiva order</h2>
        <Table responsive striped bordered hover>
          <thead>
            <tr>
              <th>Skapad</th>
              <th>Status</th>
              <th>Kund</th>
              <th>Reg.nr</th>
              <th>AO-nummer</th>
              <th>Redigeras av</th>
              <th>Exporterad</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {ordersByUserId.map((order) => (
              <tr
                key={order.id}
                role="button"
                onClick={() => openOrderHandler(order)}
              >
                <td>
                  {moment
                    .utc(order.createdAt)
                    .tz("Europe/Stockholm")
                    .format("yyyy-MM-DD HH:mm")}
                </td>
                <td>{order.orderStatus?.name}</td>
                <td>{order.customer?.name}</td>
                <td>{order.registrationNumber}</td>
                <td>{order.aoNumber}</td>
                <td>{order.userName}</td>
                <td>{order.exported && <Check className="text-success" />}</td>
                <td align="center">
                  <Eye />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {loadingOrders && (
          <>
            <Row>
              <Col className="text-center">
                <Spinner variant="primary" />
              </Col>
            </Row>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
